import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { Progress } from '@backstage/core-components';
import { createInstance, i18n as I18n, TFunction } from 'i18next';
import {
  I18nextProvider,
  initReactI18next,
  useTranslation,
} from 'react-i18next';
import {
  defaultLocale,
  formatCurrency,
  formatNumber,
  formatUtcDate,
  formatUtcDateTime,
  normalizeLocale,
  serviceRegistryResources,
  ServiceRegistryLocale,
} from '@internal/service-registry-i18n';

export interface ServiceRegistryProfile {
  fullName: string;
  email: string;
  organization: string;
  contactPhone: string;
  jobTitle: string;
  department: string;
  manager: {
    name: string;
    email: string;
    phone: string;
    title: string;
  };
}

export interface ServiceRegistryProfileInput {
  contactPhone: string;
  jobTitle: string;
  department: string;
  manager: ServiceRegistryProfile['manager'];
  preferredLocale: ServiceRegistryLocale;
}

interface ProfileBootstrapResponse {
  isFirstLogin: boolean;
  profileComplete: boolean;
  suggestedLocale: ServiceRegistryLocale;
  preferredLocale?: ServiceRegistryLocale | null;
  profile: ServiceRegistryProfile;
}

interface LanguageContextValue {
  locale: ServiceRegistryLocale;
  loading: boolean;
  error?: string;
  profile?: ServiceRegistryProfile;
  profileComplete: boolean;
  isFirstLogin: boolean;
  saveProfile(input: ServiceRegistryProfileInput): Promise<void>;
}

const languageContext = createContext<LanguageContextValue | undefined>(
  undefined,
);

const serviceRegistryI18n = createInstance();
void serviceRegistryI18n.use(initReactI18next).init({
  resources: serviceRegistryResources,
  lng: defaultLocale,
  fallbackLng: defaultLocale,
  supportedLngs: ['en-CA', 'fr-CA'],
  defaultNS: 'serviceRegistry',
  ns: ['serviceRegistry'],
  interpolation: { escapeValue: false },
  initImmediate: false,
});

export const ServiceRegistryPublicLanguageProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [locale] = useState(() =>
    normalizeLocale(
      typeof navigator === 'undefined' ? defaultLocale : navigator.language,
    ),
  );

  useEffect(() => {
    void serviceRegistryI18n.changeLanguage(locale);
  }, [locale]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      loading: false,
      profileComplete: false,
      isFirstLogin: false,
      saveProfile: async () => {
        throw new Error('UNAUTHORIZED');
      },
    }),
    [locale],
  );

  return (
    <I18nextProvider i18n={serviceRegistryI18n}>
      <languageContext.Provider value={value}>
        {children}
      </languageContext.Provider>
    </I18nextProvider>
  );
};

const errorCode = async (response: Response): Promise<string> => {
  try {
    const body = await response.json();
    return body?.error?.code || `HTTP_${response.status}`;
  } catch (_error) {
    return `HTTP_${response.status}`;
  }
};

export const ServiceRegistryLanguageProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const [locale, setLocale] = useState<ServiceRegistryLocale>(defaultLocale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [profile, setProfile] = useState<ServiceRegistryProfile>();
  const [profileComplete, setProfileComplete] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const baseUrl = configApi.getString('backend.baseUrl');

  const getToken = useCallback(async () => {
    const credentials = await identityApi.getCredentials();
    if (!credentials.token) throw new Error('UNAUTHORIZED');
    return credentials.token;
  }, [identityApi]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      try {
        const token = await getToken();
        const detectedLocale = normalizeLocale(navigator.language);
        const response = await fetch(`${baseUrl}/api/users/me/bootstrap`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ detectedLocale }),
        });
        if (!response.ok) throw new Error(await errorCode(response));
        const result = (await response.json()) as ProfileBootstrapResponse;
        if (!active) return;

        const activeLocale = normalizeLocale(
          result.preferredLocale || result.suggestedLocale,
        );
        await serviceRegistryI18n.changeLanguage(activeLocale);
        if (!active) return;
        setLocale(activeLocale);
        setProfile(result.profile);
        setProfileComplete(result.profileComplete);
        setIsFirstLogin(result.isFirstLogin);
        setError(undefined);
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : String(caught));
      } finally {
        if (active) setLoading(false);
      }
    };

    void bootstrap();
    return () => {
      active = false;
    };
  }, [baseUrl, getToken]);

  const saveProfile = useCallback(
    async (input: ServiceRegistryProfileInput) => {
      const token = await getToken();
      const response = await fetch(`${baseUrl}/api/users/me/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await errorCode(response));

      const result = await response.json();
      await serviceRegistryI18n.changeLanguage(input.preferredLocale);
      setLocale(input.preferredLocale);
      setProfileComplete(Boolean(result.profileComplete));
      setIsFirstLogin(false);
      setProfile(current =>
        current
          ? {
              ...current,
              contactPhone: input.contactPhone,
              jobTitle: input.jobTitle,
              department: input.department,
              manager: input.manager,
            }
          : current,
      );
    },
    [baseUrl, getToken],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      locale,
      loading,
      error,
      profile,
      profileComplete,
      isFirstLogin,
      saveProfile,
    }),
    [
      error,
      isFirstLogin,
      loading,
      locale,
      profile,
      profileComplete,
      saveProfile,
    ],
  );

  return (
    <I18nextProvider i18n={serviceRegistryI18n}>
      <languageContext.Provider value={value}>
        {loading ? <Progress /> : children}
      </languageContext.Provider>
    </I18nextProvider>
  );
};

export const useServiceRegistryLanguage = (): LanguageContextValue => {
  const value = useContext(languageContext);
  if (!value) {
    throw new Error(
      'useServiceRegistryLanguage must be used within ServiceRegistryLanguageProvider',
    );
  }
  return value;
};

export interface ServiceRegistryTranslation {
  t: TFunction;
  i18n: I18n;
  locale: ServiceRegistryLocale;
  formatNumber(
    value: number | bigint,
    options?: Intl.NumberFormatOptions,
  ): string;
  formatCurrency(
    value: number | bigint,
    currency: string,
    options?: Intl.NumberFormatOptions,
  ): string;
  formatUtcDate(value: string | number | Date): string;
  formatUtcDateTime(
    value: string | number | Date,
    includeSeconds?: boolean,
  ): string;
}

export const useServiceRegistryTranslation = (): ServiceRegistryTranslation => {
  const { locale } = useServiceRegistryLanguage();
  const translation = useTranslation('serviceRegistry', {
    i18n: serviceRegistryI18n,
  });
  return {
    ...translation,
    locale,
    formatNumber: (value, options) => formatNumber(value, locale, options),
    formatCurrency: (value, currency, options) =>
      formatCurrency(value, currency, locale, options),
    formatUtcDate,
    formatUtcDateTime,
  };
};
